/**
 * Upload Session Store
 *
 * Zustand store for persisting upload session state across sheet open/close cycles.
 * Tracks files, their upload status, documentIds (set after upload), and session phase.
 * Processing status is NOT stored here - it's derived from Convex real-time subscriptions.
 */

import { create } from "zustand";
import type { Id } from "@/packages/types";

export type UploadSessionFileStatus =
  | "pending"
  | "uploading"
  | "done"
  | "error";

export type UploadSessionPhase =
  | "idle"
  | "selecting"
  | "uploading"
  | "processing"
  | "completed";

export interface UploadSessionFile {
  localId: string;
  fileName: string;
  fileType: string;
  tags: string[];
  documentId?: Id<"documents">;
  categoryId?: Id<"documentCategories">;
  expirationDate?: number;
  uploadStatus: UploadSessionFileStatus;
  uploadProgress: number;
  errorMessage?: string;
}

interface UploadSessionStore {
  files: UploadSessionFile[];
  phase: UploadSessionPhase;
  selectedAuditId: Id<"audits"> | null;
  explicitNoAudit: boolean;
  selectedCategoryId: Id<"documentCategories"> | null;
  startedAt: number | null;

  // Actions
  addFiles: (files: UploadSessionFile[]) => void;
  removeFile: (localId: string) => void;
  setFileUploadStatus: (
    localId: string,
    status: UploadSessionFileStatus,
    progress?: number,
  ) => void;
  setFileDocumentId: (localId: string, documentId: Id<"documents">) => void;
  setFileCategoryId: (
    localId: string,
    categoryId: Id<"documentCategories">,
  ) => void;
  setFileExpirationDate: (localId: string, date: number | undefined) => void;
  renameFile: (localId: string, fileName: string) => void;
  addFileTag: (localId: string, tagId: string) => void;
  removeFileTag: (localId: string, tagId: string) => void;
  setSelectedAuditId: (id: Id<"audits"> | null) => void;
  setExplicitNoAudit: (value: boolean) => void;
  setSelectedCategoryId: (categoryId: Id<"documentCategories"> | null) => void;
  setPhase: (phase: UploadSessionPhase) => void;
  reset: () => void;
  clearStaleSession: () => void;
}

const STALE_SESSION_MS = 24 * 60 * 60 * 1000; // 24 hours
const COMPLETED_SESSION_IDLE_MS = 30 * 60 * 1000; // 30 minutes

const initialState = {
  files: [] as UploadSessionFile[],
  phase: "idle" as UploadSessionPhase,
  selectedAuditId: null as Id<"audits"> | null,
  explicitNoAudit: false,
  selectedCategoryId: null as Id<"documentCategories"> | null,
  startedAt: null as number | null,
};

export const useUploadSessionStore = create<UploadSessionStore>((set, get) => ({
  ...initialState,

  addFiles: (newFiles) =>
    set((state) => {
      const existingNames = new Set(state.files.map((f) => f.fileName));
      const deduplicated = newFiles.filter(
        (f) => !existingNames.has(f.fileName),
      );
      return {
        files: [...state.files, ...deduplicated],
        phase: state.phase === "idle" ? "selecting" : state.phase,
        startedAt: state.startedAt ?? Date.now(),
      };
    }),

  removeFile: (localId) =>
    set((state) => {
      const remaining = state.files.filter((f) => f.localId !== localId);
      return {
        files: remaining,
        phase: remaining.length === 0 ? "idle" : state.phase,
      };
    }),

  renameFile: (localId, fileName) =>
    set((state) => ({
      files: state.files.map((f) =>
        f.localId === localId ? { ...f, fileName } : f,
      ),
    })),

  setFileUploadStatus: (localId, status, progress) =>
    set((state) => ({
      files: state.files.map((f) =>
        f.localId === localId
          ? {
              ...f,
              uploadStatus: status,
              uploadProgress: progress ?? f.uploadProgress,
              errorMessage: status === "error" ? f.errorMessage : undefined,
            }
          : f,
      ),
    })),

  setFileDocumentId: (localId, documentId) =>
    set((state) => ({
      files: state.files.map((f) =>
        f.localId === localId ? { ...f, documentId } : f,
      ),
    })),

  setFileCategoryId: (localId, categoryId) =>
    set((state) => ({
      files: state.files.map((f) =>
        f.localId === localId ? { ...f, categoryId } : f,
      ),
    })),

  setFileExpirationDate: (localId, date) =>
    set((state) => ({
      files: state.files.map((f) =>
        f.localId === localId ? { ...f, expirationDate: date } : f,
      ),
    })),

  addFileTag: (localId, tagId) =>
    set((state) => ({
      files: state.files.map((f) =>
        f.localId === localId && !f.tags.includes(tagId)
          ? { ...f, tags: [...f.tags, tagId] }
          : f,
      ),
    })),

  removeFileTag: (localId, tagId) =>
    set((state) => ({
      files: state.files.map((f) =>
        f.localId === localId
          ? { ...f, tags: f.tags.filter((t) => t !== tagId) }
          : f,
      ),
    })),

  setSelectedAuditId: (id) => set({ selectedAuditId: id }),

  setExplicitNoAudit: (value) => set({ explicitNoAudit: value }),

  setSelectedCategoryId: (categoryId) =>
    set({ selectedCategoryId: categoryId }),

  setPhase: (phase) => set({ phase }),

  reset: () => set({ ...initialState }),

  clearStaleSession: () => {
    const { startedAt, phase } = get();
    // Clear sessions older than 24h
    if (startedAt && Date.now() - startedAt > STALE_SESSION_MS) {
      set({ ...initialState });
      return;
    }
    // Clear completed sessions older than 30min
    if (
      phase === "completed" &&
      startedAt &&
      Date.now() - startedAt > COMPLETED_SESSION_IDLE_MS
    ) {
      set({ ...initialState });
    }
  },
}));
